import { useState } from "react";
import toast from "react-hot-toast";
import { Plus, Trash2, Edit2, CalendarDays, Loader2, Search, ImageUp, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import {
    useAdminConferences,
    useCreateConference,
    useUpdateConference,
    useDeleteConference,
    useUploadConferencePhoto,
    type Conference,
} from "@/services/adminConferenceService";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function ConferencesPage() {
    const { data: conferences, isLoading } = useAdminConferences();
    const createConference = useCreateConference();
    const updateConference = useUpdateConference();
    const deleteConference = useDeleteConference();
    const uploadPhoto = useUploadConferencePhoto();

    const [search, setSearch] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [uploadingId, setUploadingId] = useState<number | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        slug: "",
        name: "",
        description: "",
        chief: "",
        contact_person: "",
        email: "",
        conference_url: "",
        is_active: true,
    });

    const filtered = (conferences || []).filter((c: Conference) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (c.name || "").toLowerCase().includes(s) || (c.slug || "").toLowerCase().includes(s) || (c.chief || "").toLowerCase().includes(s);
    });

    const handleOpenModal = (conference?: Conference) => {
        if (conference) {
            setEditingId(conference.conference_id);
            setFormData({
                slug: conference.slug || "",
                name: conference.name || "",
                description: conference.description || "",
                chief: conference.chief || "",
                contact_person: conference.contact_person || "",
                email: conference.email || "",
                conference_url: conference.conference_url || "",
                is_active: conference.is_active,
            });
        } else {
            setEditingId(null);
            setFormData({ slug: "", name: "", description: "", chief: "", contact_person: "", email: "", conference_url: "", is_active: true });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.slug || !formData.name) {
            toast.error("Slug dan nama conference wajib diisi.");
            return;
        }

        const payload = {
            slug: formData.slug,
            name: formData.name,
            description: formData.description || null,
            chief: formData.chief || null,
            contact_person: formData.contact_person || null,
            email: formData.email || null,
            conference_url: formData.conference_url || null,
            is_active: formData.is_active,
        };

        try {
            if (editingId) {
                await updateConference.mutateAsync({ id: editingId, payload });
                toast.success("Conference berhasil diperbarui!");
            } else {
                await createConference.mutateAsync(payload);
                toast.success("Conference berhasil ditambahkan!");
            }
            setIsModalOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Terjadi kesalahan saat menyimpan data.");
        }
    };

    const handleUploadPhoto = async (id: number, file: File) => {
        setUploadingId(id);
        try {
            await uploadPhoto.mutateAsync({ id, file });
            toast.success("Foto conference berhasil diunggah");
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Gagal mengunggah foto");
        } finally {
            setUploadingId(null);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* Header Card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <CalendarDays className="w-64 h-64 text-blue-600" />
                </div>

                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 flex-shrink-0 border border-blue-100 dark:border-blue-800/50 shadow-sm">
                        <CalendarDays className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Kelola Conference</h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Kelola master data track konferensi (nama, slug, ketua, kontak, foto) - dipakai sebagai sumber data di fitur ruangan, pembicara, presensi, dan output conference.
                        </p>
                    </div>
                </div>

                <Button onClick={() => handleOpenModal()} className="relative z-10 h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/20">
                    <Plus className="w-5 h-5 mr-2" />
                    Tambah Conference
                </Button>
            </div>

            {/* List */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800">
                    <div className="relative w-full max-w-md">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <Input
                            type="text"
                            placeholder="Cari conference..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl w-full"
                        />
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="px-6 py-12 text-center text-slate-500">Data tidak ditemukan.</div>
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
                        {filtered.map((c: Conference) => (
                            <div key={c.conference_id} className="p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0">
                                    {c.photo_path ? (
                                        <img src={c.photo_path} alt={c.name} className="w-14 h-14 rounded-2xl object-cover border border-slate-200 dark:border-slate-700 shrink-0" />
                                    ) : (
                                        <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                                            <CalendarDays className="w-6 h-6 text-slate-400" />
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="font-bold text-slate-900 dark:text-white">{c.name}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">{c.slug}{c.chief ? ` · ${c.chief}` : ""}</p>
                                        {c.conference_url && (
                                            <a href={c.conference_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mt-0.5">
                                                {c.conference_url} <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                    <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-md shrink-0 flex items-center gap-1 ${c.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>
                                        {c.is_active ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                        {c.is_active ? "Aktif" : "Nonaktif"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <label>
                                        <Button asChild variant="outline" size="icon" className="h-9 w-9 rounded-xl pointer-events-none">
                                            <span>{uploadingId === c.conference_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageUp className="w-4 h-4" />}</span>
                                        </Button>
                                        <input
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            className="hidden"
                                            onChange={(e) => e.target.files?.[0] && handleUploadPhoto(c.conference_id, e.target.files[0])}
                                        />
                                    </label>
                                    <Button variant="ghost" size="icon" onClick={() => handleOpenModal(c)} className="h-9 w-9 text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50">
                                        <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmId(c.conference_id)} className="h-9 w-9 text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30 dark:hover:bg-red-900/50">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal Tambah/Edit */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">{editingId ? "Edit Conference" : "Tambah Conference"}</DialogTitle>
                        <DialogDescription className="text-slate-500 mt-1.5">Isi detail track konferensi. Foto diunggah terpisah setelah data tersimpan.</DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[75vh]">
                        <div className="space-y-2">
                            <Label htmlFor="slug" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Slug <span className="text-red-500">*</span></Label>
                            <Input
                                id="slug"
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                placeholder="konaspi-xii"
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nama Conference <span className="text-red-500">*</span></Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ganesha International Conference on..."
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Deskripsi</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="resize-none h-24 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="chief" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Ketua</Label>
                            <Input
                                id="chief"
                                value={formData.chief}
                                onChange={(e) => setFormData({ ...formData, chief: e.target.value })}
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="contact_person" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kontak Person</Label>
                                <Input
                                    id="contact_person"
                                    value={formData.contact_person}
                                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                                    className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="conference_url" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Website Conference</Label>
                            <Input
                                id="conference_url"
                                value={formData.conference_url}
                                onChange={(e) => setFormData({ ...formData, conference_url: e.target.value })}
                                placeholder="https://..."
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                            />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                            <div className="space-y-0.5">
                                <Label className="text-sm font-bold text-slate-900 dark:text-white">Status Aktif</Label>
                                <p className="text-xs text-slate-500">Conference nonaktif tidak muncul di pilihan publik.</p>
                            </div>
                            <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                        </div>

                        <DialogFooter className="pt-4 mt-6 border-t border-slate-200 dark:border-slate-800">
                            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="h-11 px-6 rounded-xl">
                                Batal
                            </Button>
                            <Button type="submit" disabled={createConference.isPending || updateConference.isPending} className="h-11 px-6 rounded-xl bg-blue-600 hover:bg-blue-700">
                                {(createConference.isPending || updateConference.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Simpan
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm Modal */}
            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Hapus Conference?</DialogTitle>
                        <DialogDescription>Tindakan ini tidak dapat dibatalkan. Pastikan tidak ada data terkait (ruangan, pembicara, dll) yang masih bergantung pada conference ini.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Batal</Button>
                        <Button
                            variant="destructive"
                            disabled={deleteConference.isPending}
                            onClick={async () => {
                                if (!deleteConfirmId) return;
                                try {
                                    await deleteConference.mutateAsync(deleteConfirmId);
                                    toast.success("Conference berhasil dihapus!");
                                    setDeleteConfirmId(null);
                                } catch (error: any) {
                                    toast.error(error?.response?.data?.message || "Gagal menghapus conference.");
                                }
                            }}
                        >
                            {deleteConference.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Ya, Hapus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
